import { memo } from 'react';
import { motion } from 'framer-motion';

export type FlywheelEvent = 'lap' | 'boost';

/**
 * The proof line: effort that compounds (climbing, accelerating) vs effort that
 * resets (flat, going nowhere). Built deterministically from the event history.
 */
export const CompoundingChart = memo(function CompoundingChart({
  events,
  reducedMotion,
}: {
  events: FlywheelEvent[];
  reducedMotion: boolean;
}) {
  const W = 280;
  const H = 122;
  const pad = 12;

  const comp: number[] = [0];
  const res: number[] = [0];
  let v = 0;
  let lap = 0;
  for (const e of events) {
    if (e === 'lap') {
      lap += 1;
      v += 1 + lap * 0.6; // each lap adds more than the last — compounding
    } else {
      v += 9; // a model upgrade jolts it up
    }
    comp.push(v);
    res.push(1.1 + (comp.length % 2 ? 0.5 : 0)); // sawtooth that never accumulates
  }

  const n = comp.length;
  const maxV = Math.max(5, ...comp);
  const x = (i: number) => pad + (n <= 1 ? 0 : (i / (n - 1)) * (W - 2 * pad));
  const y = (val: number) => H - pad - (val / maxV) * (H - 2 * pad);
  const line = (arr: number[]) =>
    arr.map((val, i) => `${i === 0 ? 'M' : 'L'} ${x(i).toFixed(1)} ${y(val).toFixed(1)}`).join(' ');
  const area =
    n > 1
      ? `${line(comp)} L ${x(n - 1).toFixed(1)} ${(H - pad).toFixed(1)} L ${x(0).toFixed(1)} ${(H - pad).toFixed(1)} Z`
      : '';

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="h-auto w-full" role="img" aria-label="Effort that compounds versus effort that resets">
      <line x1={pad} y1={H - pad} x2={W - pad} y2={H - pad} stroke="var(--fd-border)" strokeWidth={1} />

      {/* resets: flat, dashed, going nowhere */}
      <path d={line(res)} fill="none" stroke="var(--fd-coral)" strokeOpacity={0.6} strokeWidth={1.5} strokeDasharray="3 4" />

      {/* compounds: yellow energy under an ink line for contrast */}
      {area && <path d={area} fill="var(--fd-accent-soft)" />}
      <path d={line(comp)} fill="none" stroke="var(--fd-text)" strokeWidth={2.25} strokeLinejoin="round" strokeLinecap="round" />

      {n > 1 && (
        <motion.circle
          cx={x(n - 1)}
          cy={y(comp[n - 1])}
          r={4.5}
          fill="var(--fd-accent)"
          stroke="var(--fd-text)"
          strokeWidth={1.25}
          animate={reducedMotion ? undefined : { scale: [1, 1.45, 1] }}
          transition={{ repeat: Infinity, duration: 1.7, ease: 'easeInOut' }}
          style={{ transformOrigin: `${x(n - 1)}px ${y(comp[n - 1])}px` }}
        />
      )}
    </svg>
  );
});
